import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { api, setAnonId, setUserToken } from "../../lib/api";

/**
 * `/control/:token` — where a scanned "control from your phone" QR lands.
 *
 * The phone arrives with nothing and leaves as the same operator the desktop was. For a
 * signed-in owner that means a real session; for a signed-out one it means adopting the
 * anon id, because that IS the identity and a phone without it would arrive a stranger
 * looking at an empty library.
 *
 * A hard reload rather than a client-side navigate: identity is read at module load in
 * several places — the socket's hello, the anon id, the session store — and half of them
 * would keep the identity this tab started with. Reloading is the only way to be sure
 * every one of them sees the new one.
 */
export function AdoptControlScreen() {
  const { token } = useParams<{ token: string }>();
  const [error, setError] = useState<string | null>(null);
  const adopted = useRef(false);

  useEffect(() => {
    if (!token || adopted.current) return;
    // One-shot on the server, so a StrictMode double-invoke would spend the token on the
    // first pass and report the second as expired.
    adopted.current = true;
    void api.control
      .adopt(token)
      .then((r) => {
        if (r.token) setUserToken(r.token);
        else if (r.anonId) setAnonId(r.anonId);
        // Straight to the remote, not the library: this QR exists to put the
        // controller in somebody's hand, and landing on a page you scroll would make
        // them go find it.
        window.location.replace("/remote");
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, [token]);

  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center gap-3 px-6 text-center">
      {error ? (
        <>
          <span className="text-lg text-zinc-200">Couldn&rsquo;t take over control</span>
          <span className="max-w-sm text-sm text-zinc-500">{error}</span>
          <span className="max-w-sm text-xs text-zinc-600">
            These links last two minutes and work once. Show a fresh one from the other
            device.
          </span>
        </>
      ) : (
        <>
          <span className="h-6 w-6 animate-spin rounded-full border-2 border-[var(--color-accent)]/25 border-t-[var(--color-accent)]" />
          <span className="text-sm text-zinc-500">Taking over…</span>
        </>
      )}
    </div>
  );
}
